import React from 'react';
import PageTransition from '@/components/ui/PageTransition';
import { PageHeader } from '@/components/ui/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
import { StatCardGrid } from '@/components/ui/StatCardGrid';
import {
  Typography,
  Empty,
  Button,
  Card,
  Table,
  Tag,
  Flex,
  theme
} from 'antd';
import {
  ReloadOutlined,
  ClearOutlined,
  CloseCircleOutlined,
  TeamOutlined,
  UserAddOutlined,
  StarOutlined,
  TrophyOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  BulkActionBar,
  DeleteEntityModal,
  SELECTION_COLUMN_WIDTH
} from '@/components/shared';
import type { LeadStatus } from '@/api/mcp-crm/interfaces';
import { LeadFilters } from '@/components/leads/LeadFilters';
import { useLeadColumns } from '@/components/leads/useLeadColumns';
import { useLeadsList } from '@/components/leads/useLeadsList';

const { Text } = Typography;

export const Route = createFileRoute('/manage/leads/')({
  component: LeadsListComponent,
});

function LeadsListComponent() {
  const { t } = useTranslation('manage');
  const { token } = theme.useToken();
  const {
    leads,
    filteredLeads,
    stats,
    isLoading,
    isError,
    isFetching,
    refetch,
    searchText,
    setSearchText,
    statusFilter,
    setStatusFilter,
    channelFilter,
    setChannelFilter,
    hasActiveFilters,
    resetFilters,
    toggleStatusFilter,
    canDelete,
    selectedLeadIds,
    setSelectedLeadIds,
    leadDeletion,
    loadLeadDeletionPreview,
    deleteLeadsMutation,
    confirmConvertLead,
    convertingLeadId
  } = useLeadsList();

  const columns = useLeadColumns({
    canDelete,
    convertingLeadId,
    onConvert: confirmConvertLead,
    onDelete: (leadId) => leadDeletion.request([leadId])
  });

  const header = (
    <PageHeader icon={<TeamOutlined />} title={t('leads.title')} description={t('leads.description')} />
  );

  // Error state
  if (isError) {
    return (
      <PageTransition animation="fadeInUp">
        {header}
        <Card>
          <Empty description={t('leads.loadError')} image={Empty.PRESENTED_IMAGE_SIMPLE}>
            <Button type="primary" icon={<ReloadOutlined />} onClick={() => refetch()}>
              {t('leads.tryAgain')}
            </Button>
          </Empty>
        </Card>
      </PageTransition>
    );
  }

  const statCards: { status?: LeadStatus; label: string; value: number; icon: React.ReactNode; color: string }[] = [
    { label: t('leads.stats.totalLeads'), value: stats.total, icon: <TeamOutlined />, color: token.colorPrimary },
    { status: 'NEW', label: t('leads.stats.newLeads'), value: stats.new, icon: <UserAddOutlined />, color: token.colorInfo },
    { status: 'QUALIFIED', label: t('leads.stats.qualifiedLeads'), value: stats.qualified, icon: <StarOutlined />, color: token.colorWarning },
    { status: 'DISQUALIFIED', label: t('leads.stats.disqualifiedLeads'), value: stats.disqualified, icon: <CloseCircleOutlined />, color: token.colorTextTertiary },
    { status: 'CONVERTED', label: t('leads.stats.convertedLeads'), value: stats.converted, icon: <TrophyOutlined />, color: token.colorSuccess }
  ];

  return (
    <PageTransition animation="fadeInUp">
      {header}

      <StatCardGrid minCardWidth={180}>
        {statCards.map((card) => (
          <StatCard
            key={card.label}
            label={card.label}
            value={card.value}
            icon={card.icon}
            color={card.color}
            loading={isLoading}
            active={!!card.status && statusFilter === card.status}
            onClick={card.status ? () => toggleStatusFilter(card.status as LeadStatus) : resetFilters}
          />
        ))}
      </StatCardGrid>

      <LeadFilters
        searchText={searchText}
        onSearchTextChange={setSearchText}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        channelFilter={channelFilter}
        onChannelFilterChange={setChannelFilter}
        hasActiveFilters={hasActiveFilters}
        filteredCount={filteredLeads.length}
        totalCount={leads.length}
        onReset={resetFilters}
        isFetching={isFetching}
        onRefresh={() => refetch()}
      />

      {/* Leads Table */}
      <Card
        title={
          <Flex align="center" gap={token.marginXS}>
            <span>{t('leads.table.title')}</span>
            {!isLoading && (
              <Tag color={hasActiveFilters ? 'blue' : 'default'} style={{ margin: 0 }}>
                {filteredLeads.length === 1
                  ? t('leads.table.leadCount', { count: filteredLeads.length })
                  : t('leads.table.leadCount_plural', { count: filteredLeads.length })}
              </Tag>
            )}
          </Flex>
        }
        styles={{ body: { padding: 0 } }}
      >
        {selectedLeadIds.length > 0 && (
          <div style={{ padding: `${token.paddingSM}px ${token.paddingSM}px 0` }}>
            <BulkActionBar selectedCount={selectedLeadIds.length} onClearSelection={() => setSelectedLeadIds([])}>
              <Button danger icon={<DeleteOutlined />} onClick={() => leadDeletion.request(selectedLeadIds)}>
                {t('deleteEntity.deleteSelected', { count: selectedLeadIds.length })}
              </Button>
            </BulkActionBar>
          </div>
        )}
        <Table
          columns={columns}
          dataSource={filteredLeads}
          rowKey="id"
          loading={isLoading}
          rowSelection={
            canDelete
              ? {
                  columnWidth: SELECTION_COLUMN_WIDTH,
                  selectedRowKeys: selectedLeadIds,
                  onChange: (keys) => setSelectedLeadIds(keys.map(String))
                }
              : undefined
          }
          scroll={{ x: 'max-content' }}
          pagination={{
            defaultPageSize: 20,
            showQuickJumper: true,
            responsive: true,
            showTotal: (total, range) => (
              <span style={{ color: token.colorTextSecondary }}>
                {t('leads.table.showingRange', { start: range[0], end: range[1], total })}
              </span>
            ),
          }}
          locale={{
            emptyText: isLoading ? (
              <div style={{ minHeight: 160 }} />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Flex vertical align="center" gap={token.marginXXS}>
                    <Text>{hasActiveFilters ? t('leads.table.noMatchFilters') : t('leads.table.noLeadsFound')}</Text>
                    {!hasActiveFilters && <Text type="secondary">{t('leads.table.noLeadsHint')}</Text>}
                  </Flex>
                }
              >
                {hasActiveFilters && (
                  <Button icon={<ClearOutlined />} onClick={resetFilters}>
                    {t('leads.table.clearFilters')}
                  </Button>
                )}
              </Empty>
            )
          }}
        />
      </Card>
      <DeleteEntityModal
        entity="leads"
        {...leadDeletion.modalState}
        loadPreview={loadLeadDeletionPreview}
        onConfirm={(leadIds) => deleteLeadsMutation.mutateAsync(leadIds)}
        isDeleting={deleteLeadsMutation.isPending}
      />
    </PageTransition>
  );
}
